import { useMemo } from 'react';

import { useCoverageRules } from '@/shared/hooks';
import { formatCalendarDate } from '@/shared/utils/calendar-date.util';
import { useDebounce } from './useDebounce';

type UseDynamicCoverageRulesProps = {
  propertyId?: string;
  leaseStart?: Date;
  leaseEnd?: Date;
  grossMonthlyRent?: number;
  prepaidRent?: number;
  freeRent?: number;
  enabled?: boolean;
  /**
   * When true, the fetch is blocked until lease_start/end + gross_monthly_rent
   * are all populated. SMB needs this because its options are derived from
   * `rule.pricing` — fetching without pricing yields no usable options.
   * Institutional fetches eagerly on propertyId so customized labels appear
   * as soon as the property is selected.
   */
  waitForPricingParams?: boolean;
};

/**
 * Shared fetcher for DDS dynamic coverage rules.
 * - Debounces the five lease params (600ms) to avoid request storms on typing.
 * - Only sends pricing params when all three required fields are populated.
 * Both SMB and Institutional flows consume this hook for the fetching contract;
 * they diverge in how they render the resulting rules.
 */
export const useDynamicCoverageRules = ({
  propertyId,
  leaseStart,
  leaseEnd,
  grossMonthlyRent,
  prepaidRent,
  freeRent,
  enabled = true,
  waitForPricingParams = false,
}: UseDynamicCoverageRulesProps) => {
  const debouncedLeaseStartDate = useDebounce(formatCalendarDate(leaseStart), 600);
  const debouncedLeaseEndDate = useDebounce(formatCalendarDate(leaseEnd), 600);
  const debouncedGrossMonthlyRent = useDebounce(grossMonthlyRent, 600);
  const debouncedPrepaidRent = useDebounce(prepaidRent, 600);
  const debouncedFreeRent = useDebounce(freeRent, 600);

  const pricingParams = useMemo(
    () =>
      debouncedLeaseStartDate && debouncedLeaseEndDate && debouncedGrossMonthlyRent
        ? {
            lease_start_date: debouncedLeaseStartDate,
            lease_end_date: debouncedLeaseEndDate,
            gross_monthly_rent: debouncedGrossMonthlyRent,
            prepaid_rent: debouncedPrepaidRent,
            free_rent: debouncedFreeRent,
          }
        : undefined,
    [
      debouncedFreeRent,
      debouncedGrossMonthlyRent,
      debouncedLeaseEndDate,
      debouncedLeaseStartDate,
      debouncedPrepaidRent,
    ],
  );

  return useCoverageRules(propertyId ?? '', Boolean(enabled && propertyId), {
    params: pricingParams,
    waitForRequiredParams: waitForPricingParams,
  });
};
